import { useEffect, useState } from 'react';
import { formatCurrency } from '../../utils/formatting';
import { fetchPolicies, deletePolicy, getDocumentUrl, reExtractPolicy } from '../../utils/insuranceApi';
import { annualizePremium } from '../../utils/annualizePremium';
import { useHouseholdDetail } from '../../context/HouseholdDetailContext';

const PRODUCT_LABELS = {
  auto: 'Auto', home: 'Home', umbrella: 'Umbrella',
  life: 'Life', disability: 'Disability', watercraft: 'Watercraft', wind: 'Wind',
};

const LIFE_TYPE_LABELS = {
  term: 'Term',
  whole: 'Whole Life',
  universal: 'UL',
  variable_universal: 'VUL',
  indexed_universal: 'IUL',
  other: 'Other',
};

const PREMIUM_MODE_SHORT = {
  annual: 'yr', 'semi-annual': '6mo', quarterly: 'qtr', monthly: 'mo', single: 'single',
};

// Compute the annualized premium and a per-payment subtitle from a policy.
// Prefers the type-specific fields (life_specific.premium_amount + premium_mode,
// disability_specific.*) since those carry the actual payment cadence; falls
// back to the top-level premium_total/premium_term, then to stored premium_annual.
function derivePremiumDisplay(p) {
  const ed = p.extracted_data || {};
  const sources = [
    { amt: ed.life_specific?.premium_amount, mode: ed.life_specific?.premium_mode },
    { amt: ed.disability_specific?.premium_amount, mode: ed.disability_specific?.premium_mode },
    { amt: ed.premium_total, mode: ed.premium_term },
  ].filter(s => s.amt != null && Number(s.amt) > 0);

  const primary = sources[0];
  if (primary) {
    const annual = annualizePremium(primary.amt, primary.mode);
    const modeNorm = String(primary.mode || '').toLowerCase().trim();
    let perPaymentLabel = null;
    // Show "$X / mo" subtitle whenever the source isn't already annual.
    if (modeNorm.includes('month') && !modeNorm.includes('12') && !modeNorm.includes('6')) {
      perPaymentLabel = `($${Number(primary.amt).toLocaleString()}/mo)`;
    } else if (modeNorm.includes('quarter')) {
      perPaymentLabel = `($${Number(primary.amt).toLocaleString()}/qtr)`;
    } else if (modeNorm.includes('semi') || modeNorm === '6 month') {
      perPaymentLabel = `($${Number(primary.amt).toLocaleString()}/6mo)`;
    }
    return { annual: annual || p.premium_annual || 0, perPaymentLabel };
  }
  // No raw extraction data — use stored value as-is.
  return { annual: p.premium_annual || 0, perPaymentLabel: null };
}

const STATUS_COLORS = {
  active: 'bg-deep-teal/15 text-deep-teal',
  expired: 'bg-rose/15 text-rose',
  cancelled: 'bg-rose/15 text-rose',
  lapsed: 'bg-amber-100 text-amber-700',
  prospect: 'bg-bullogic-blue/15 text-bullogic-blue',
};

// Lifecycle is computed from dates by the API
const LIFECYCLE_BADGES = {
  in_force: { label: 'In Force', color: 'bg-deep-teal/15 text-deep-teal' },
  future_effective: { label: 'Future Effective', color: 'bg-amber-100 text-amber-700' },
  expired: { label: 'Expired', color: 'bg-rose/15 text-rose' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-200 text-gray-600' },
  lapsed: { label: 'Lapsed', color: 'bg-amber-100 text-amber-700' },
};

const FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'in_force', label: 'In Force' },
  { key: 'future_effective', label: 'Future' },
  { key: 'expired', label: 'Expired' },
  { key: 'we_broker', label: 'We Broker' },
];

export default function PolicyList({ onEdit, stateFilter: stateFilterProp, onStateFilterChange }) {
  const { openHousehold } = useHouseholdDetail();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState('all');
  const [stateFilterLocal, setStateFilterLocal] = useState('');
  // If a parent controls the state filter, use that; otherwise fall back to local state.
  const stateFilter = stateFilterProp !== undefined ? stateFilterProp : stateFilterLocal;
  const setStateFilter = onStateFilterChange || setStateFilterLocal;
  const [reExtractingId, setReExtractingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPolicies()
      .then(data => { if (!cancelled) { setPolicies(data); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [reloadKey]);

  async function handleDelete(policy) {
    const label = `${policy.product_type} ${policy.carrier_name ? '(' + policy.carrier_name + ')' : ''} for ${policy.household_name}`;
    if (!window.confirm(`Delete this policy?\n\n${label}\n\nThis can't be undone.`)) return;
    try {
      await deletePolicy(policy.id);
      setReloadKey(k => k + 1);
    } catch (err) {
      window.alert(`Delete failed: ${err.message}`);
    }
  }

  async function handleReExtract(policy) {
    if (!policy.source_document_id) {
      window.alert('No source PDF on file for this policy.');
      return;
    }
    if (!window.confirm('Re-run AI extraction on the source PDF? This will overwrite the current extracted fields (your manual edits to extracted_data may be lost).')) return;
    setReExtractingId(policy.id);
    const maxAttempts = 3;
    let lastErr = null;
    try {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          await reExtractPolicy(policy.id);
          setReloadKey(k => k + 1);
          return;
        } catch (err) {
          lastErr = err;
          const msg = err.message || '';
          const isRateLimit = msg.includes('429') || msg.includes('rate_limit');
          if (!isRateLimit || attempt === maxAttempts) throw err;
          // Parse "Please try again in Xs" or "X seconds" out of the message
          const m = msg.match(/(\d+)\s*(?:seconds?|s\b)/i);
          const waitSec = m ? Math.min(120, Math.max(5, parseInt(m[1]))) : 35;
          await new Promise(r => setTimeout(r, waitSec * 1000));
        }
      }
    } catch (err) {
      window.alert(`Re-extract failed: ${err.message}`);
    } finally {
      setReExtractingId(null);
    }
  }

  async function handleViewPDF(policy) {
    if (!policy.source_document_id) {
      window.alert('No source PDF on file for this policy.');
      return;
    }
    try {
      const url = await getDocumentUrl(policy.source_document_id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      window.alert(`Could not open PDF: ${err.message}`);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-12 text-center">
        <p className="text-[12px] text-[#888]">Loading policies...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose/5 border border-rose/20 rounded-[4px] p-4">
        <p className="text-[12px] text-rose font-bold">Error loading policies</p>
        <p className="text-[11px] text-rose mt-1">{error}</p>
      </div>
    );
  }

  if (policies.length === 0) {
    return (
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-12 text-center">
        <div className="text-[40px] mb-3 opacity-30">📄</div>
        <p className="text-[14px] text-navy font-bold mb-1">No policies yet</p>
        <p className="text-[12px] text-[#888] mb-4">
          Upload your first policy PDF to get started.
          We'll extract the data automatically.
        </p>
      </div>
    );
  }

  // Apply filter (lifecycle/broker)
  const filteredByLifecycle = policies.filter(p => {
    if (filter === 'all') return true;
    if (filter === 'we_broker') return p.we_broker;
    return p.lifecycle === filter;
  });

  // Apply state filter
  const filtered = stateFilter
    ? filteredByLifecycle.filter(p => (p.extracted_data?.address?.state || '').toUpperCase() === stateFilter)
    : filteredByLifecycle;

  // Build the list of states present (for the dropdown), sorted
  const statesPresent = Array.from(new Set(
    policies.map(p => (p.extracted_data?.address?.state || '').toUpperCase()).filter(Boolean)
  )).sort();

  // Group by household
  const byHousehold = {};
  for (const p of filtered) {
    const key = p.household_id || 'unassigned';
    if (!byHousehold[key]) byHousehold[key] = [];
    byHousehold[key].push(p);
  }

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTER_OPTIONS.map(opt => {
          const count = opt.key === 'all' ? policies.length
            : opt.key === 'we_broker' ? policies.filter(p => p.we_broker).length
            : policies.filter(p => p.lifecycle === opt.key).length;
          const active = filter === opt.key;
          return (
            <button
              key={opt.key}
              onClick={() => setFilter(opt.key)}
              className={`px-3 py-1 text-[11px] font-bold rounded-[3px] transition-colors ${
                active ? 'bg-navy text-white' : 'bg-white text-[#888] hover:text-navy border border-gray-200'
              }`}
            >
              {opt.label} <span className={`ml-1 ${active ? 'text-white/70' : 'text-[#aaa]'}`}>({count})</span>
            </button>
          );
        })}
        {statesPresent.length > 0 && (
          <select
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
            className="px-2 py-1 text-[11px] font-bold rounded-[3px] bg-white text-navy border border-gray-200 cursor-pointer ml-2"
          >
            <option value="">All States</option>
            {statesPresent.map(st => <option key={st} value={st}>{st}</option>)}
          </select>
        )}
      </div>

      {Object.entries(byHousehold).map(([householdId, hhPolicies]) => (
        <div key={householdId} className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] overflow-hidden">
          <div className="px-4 py-3 bg-bone/40 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-[13px] font-bold">
              <button
                onClick={() => openHousehold(householdId)}
                className="text-navy hover:text-bullogic-blue hover:underline transition-colors text-left"
                title="Open household card"
              >
                {hhPolicies[0]?.household_name || householdId}
              </button>
            </h3>
            <span className="text-[11px] text-[#888]">
              {hhPolicies.length} polic{hhPolicies.length === 1 ? 'y' : 'ies'} ·
              {' '}{formatCurrency(hhPolicies.reduce((s, p) => s + derivePremiumDisplay(p).annual, 0))}/yr
            </span>
          </div>
          <table className="w-full text-[12px] table-fixed">
            <colgroup>
              <col style={{ width: '32px' }} />        {/* expand caret */}
              <col style={{ width: '9%' }} />         {/* Type */}
              <col style={{ width: '17%' }} />        {/* Carrier */}
              <col style={{ width: '17%' }} />        {/* Insured */}
              <col style={{ width: '6%' }} />         {/* State */}
              <col style={{ width: '13%' }} />        {/* Policy # */}
              <col style={{ width: '9%' }} />         {/* Effective */}
              <col style={{ width: '9%' }} />         {/* Expires */}
              <col style={{ width: '9%' }} />         {/* Premium */}
              <col style={{ width: '11%' }} />        {/* Status */}
            </colgroup>
            <thead className="bg-gray-50">
              <tr className="text-left">
                <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#888]"></th>
                <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#888]">Type</th>
                <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#888]">Carrier</th>
                <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#888]">Insured</th>
                <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#888]">State</th>
                <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#888]">Policy #</th>
                <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#888]">Effective</th>
                <th className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#888]">Expires</th>
                <th className="px-4 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-[#888]">Premium</th>
                <th className="px-4 py-2 text-center text-[10px] font-bold uppercase tracking-wider text-[#888]">Status</th>
              </tr>
            </thead>
            <tbody>
              {hhPolicies.map(p => (
                <PolicyRow
                  key={p.id}
                  policy={p}
                  isExpanded={expandedId === p.id}
                  onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                  onEdit={onEdit}
                  onDelete={handleDelete}
                  onViewPDF={handleViewPDF}
                  onReExtract={handleReExtract}
                  reExtracting={reExtractingId === p.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ─── Single policy row with expandable detail ────────────────────────
function PolicyRow({ policy: p, isExpanded, onToggle, onEdit, onDelete, onViewPDF, onReExtract, reExtracting }) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="border-t border-gray-100 hover:bg-bone/30 cursor-pointer"
      >
        <td className="px-4 py-2 text-center text-[10px] text-[#aaa]">
          {isExpanded ? '▼' : '▶'}
        </td>
        <td className="px-4 py-2 font-bold text-navy truncate">
          {PRODUCT_LABELS[p.product_type] || p.product_type}
          {p.vehicle_count != null && (
            <span className="text-[10px] text-[#888] font-normal ml-1">({p.vehicle_count} veh)</span>
          )}
        </td>
        <td className="px-4 py-2 truncate" title={p.carrier_name || ''}>{p.carrier_name || '--'}</td>
        <td className="px-4 py-2 text-[11px] truncate" title={p.named_insured_name || ''}>{p.named_insured_name || '--'}</td>
        <td className="px-4 py-2 text-[11px] font-bold text-navy">{p.extracted_data?.address?.state || '--'}</td>
        <td className="px-4 py-2 font-mono text-[11px] truncate" title={p.policy_number || ''}>{p.policy_number || '--'}</td>
        <td className="px-4 py-2">{p.effective_date || '--'}</td>
        <td className="px-4 py-2">{p.expiration_date || '--'}</td>
        <td className="px-4 py-2 text-right font-bold">
          {(() => {
            const display = derivePremiumDisplay(p);
            return (
              <>
                <div>{formatCurrency(display.annual)}<span className="text-[10px] text-[#888] font-normal ml-1">/yr</span></div>
                {display.perPaymentLabel && (
                  <div className="text-[10px] text-[#888] font-normal">{display.perPaymentLabel}</div>
                )}
              </>
            );
          })()}
        </td>
        <td className="px-4 py-2 text-center">
          <div className="flex flex-col items-center gap-0.5">
            {(() => {
              const lc = LIFECYCLE_BADGES[p.lifecycle];
              return lc ? (
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-[3px] ${lc.color}`}>
                  {lc.label}
                </span>
              ) : (
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-[3px] ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                  {p.status}
                </span>
              );
            })()}
            {p.we_broker && (
              <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-[2px] bg-bullogic-blue/15 text-bullogic-blue">
                Broker
              </span>
            )}
          </div>
          {/* Action callout for future-effective: highlight that we should shop */}
          {p.lifecycle === 'future_effective' && p.days_until_effective != null && p.days_until_effective <= 30 && (
            <div className="mt-1 text-[9px] text-amber-700 font-bold">
              Effective in {p.days_until_effective}d -- shop now
            </div>
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-bone/20">
          <td colSpan={10} className="px-6 py-4">
            <PolicyDetail policy={p} />
            {/* Action bar */}
            <div className="mt-4 pt-3 border-t border-gray-200 flex items-center gap-2">
              {p.source_document_id && (
                <button
                  onClick={(e) => { e.stopPropagation(); onViewPDF?.(p); }}
                  className="px-3 py-1.5 text-[11px] font-bold text-bullogic-blue bg-bullogic-blue/10 hover:bg-bullogic-blue/20 rounded-[3px]"
                >
                  View Source PDF
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onEdit?.(p); }}
                className="px-3 py-1.5 text-[11px] font-bold text-navy bg-gray-100 hover:bg-gray-200 rounded-[3px]"
              >
                Edit
              </button>
              {p.source_document_id && (
                <button
                  onClick={(e) => { e.stopPropagation(); onReExtract?.(p); }}
                  disabled={reExtracting}
                  className={`px-3 py-1.5 text-[11px] font-bold rounded-[3px] ${
                    reExtracting
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'text-deep-teal bg-deep-teal/10 hover:bg-deep-teal/20'
                  }`}
                  title="Re-run AI extraction on the source PDF"
                >
                  {reExtracting ? 'Re-extracting...' : 'Re-extract'}
                </button>
              )}
              <div className="flex-1" />
              <button
                onClick={(e) => { e.stopPropagation(); onDelete?.(p); }}
                className="px-3 py-1.5 text-[11px] font-bold text-rose hover:bg-rose/10 rounded-[3px]"
              >
                Delete Policy
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Format a deductible as "$X,XXX (Y%)" when we know the percentage,
// either from explicit extraction (pct) or inferred from dwelling coverage.
// Falls back to "$X,XXX" alone when no usable percentage is available.
function formatDeductible(amount, explicitPct, dwellingCoverage) {
  if (!amount) return null;
  let pct = explicitPct;
  if (pct == null && dwellingCoverage > 0) {
    const inferred = amount / dwellingCoverage;
    // Only show inferred % if it lands on a sensible round value (.5%, 1%, 2%, 5%, 10%)
    const rounded = Math.round(inferred * 200) / 200; // round to nearest 0.5%
    if (Math.abs(inferred - rounded) < 0.001 && rounded >= 0.005 && rounded <= 0.1) {
      pct = rounded;
    }
  }
  const dollars = `$${amount.toLocaleString()}`;
  if (pct != null) return `${dollars} (${(pct * 100).toFixed(pct < 0.01 ? 2 : 1).replace(/\.0$/, '')}%)`;
  return dollars;
}

// ─── Expanded detail view ────────────────────────────────────────────
function PolicyDetail({ policy: p }) {
  const ed = p.extracted_data || {};
  const ni = ed.named_insured || {};
  const addr = ed.address || {};
  const auto = ed.auto_specific;
  const home = ed.home_specific;
  const life = ed.life_specific;
  const dis = ed.disability_specific;

  return (
    <div className="space-y-4 text-[13px] text-[#333]">
      {/* Top row: Insured + Address */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5">Named Insured</div>
          <div>
            {ni.first_name} {ni.middle_name} {ni.last_name} {ni.suffix}
            {ni.dob && <span className="text-[#888]"> · DOB {ni.dob}</span>}
          </div>
          {ni.email && <div className="text-[#888]">{ni.email}</div>}
          {ni.phone && <div className="text-[#888]">{ni.phone}</div>}
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5">Address</div>
          <div>{addr.street}</div>
          <div>{addr.city}{addr.city && ', '}{addr.state} {addr.zip}</div>
          {addr.county && <div className="text-[#888]">{addr.county} County</div>}
        </div>
      </div>

      {/* Co-insureds */}
      {ed.co_insureds && ed.co_insureds.length > 0 && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5">Co-Insureds</div>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {ed.co_insureds.map((c, i) => (
              <span key={i}>{c.first_name} {c.last_name} {c.relation && <span className="text-[#888]">({c.relation})</span>}</span>
            ))}
          </div>
        </div>
      )}

      {/* Auto specifics */}
      {auto && (
        <>
          {auto.vehicles && auto.vehicles.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5">Vehicles</div>
              <table className="text-[12px] w-full">
                <thead>
                  <tr className="text-[#888]">
                    <th className="text-left pr-4 pb-1.5">Vehicle</th>
                    <th className="text-left pr-4 pb-1.5">VIN</th>
                    <th className="text-left pr-4 pb-1.5">Use</th>
                    <th className="text-left pr-4 pb-1.5">BI/PD</th>
                    <th className="text-right pb-1.5">Comp/Coll Ded</th>
                  </tr>
                </thead>
                <tbody>
                  {auto.vehicles.map((v, i) => (
                    <tr key={i}>
                      <td className="pr-4 py-0.5">{v.year} {v.make} {v.model} {v.trim}</td>
                      <td className="pr-4 py-0.5 font-mono text-[12px]">{v.vin || '--'}</td>
                      <td className="pr-4 py-0.5">{v.use || '--'}</td>
                      <td className="pr-4 py-0.5">{v.coverages?.BI_limit || '--'} / {v.coverages?.PD_limit || '--'}</td>
                      <td className="text-right py-0.5">${(v.coverages?.comp_deductible || 0)} / ${(v.coverages?.collision_deductible || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {auto.drivers && auto.drivers.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5">Drivers</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                {auto.drivers.map((d, i) => (
                  <span key={i}>{d.first_name} {d.last_name} {d.dob && <span className="text-[#888]">(DOB {d.dob})</span>}</span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Home specifics */}
      {home && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5">Home Details</div>
          <div className="grid grid-cols-4 gap-x-5 gap-y-1.5">
            {home.year_built && <div><span className="text-[#888]">Built:</span> {home.year_built}</div>}
            {home.square_feet && <div><span className="text-[#888]">Sqft:</span> {home.square_feet.toLocaleString()}</div>}
            {home.stories && <div><span className="text-[#888]">Stories:</span> {home.stories}</div>}
            {home.policy_form && <div><span className="text-[#888]">Form:</span> {home.policy_form}</div>}
            {home.construction_type && <div><span className="text-[#888]">Build:</span> {home.construction_type}</div>}
            {home.roof_type && <div><span className="text-[#888]">Roof:</span> {home.roof_type}</div>}
            {home.roof_age_years && <div><span className="text-[#888]">Roof Age:</span> {home.roof_age_years} yrs</div>}
            {home.bedrooms && <div><span className="text-[#888]">Beds/Baths:</span> {home.bedrooms}/{home.bathrooms || '?'}</div>}
            {home.dwelling_coverage && <div><span className="text-[#888]">Dwelling:</span> ${home.dwelling_coverage.toLocaleString()}</div>}
            {home.personal_property_coverage && <div><span className="text-[#888]">Personal:</span> ${home.personal_property_coverage.toLocaleString()}</div>}
            {home.liability_coverage && <div><span className="text-[#888]">Liability:</span> ${home.liability_coverage.toLocaleString()}</div>}
            {home.deductible && <div><span className="text-[#888]">Ded:</span> {formatDeductible(home.deductible, home.deductible_pct, home.dwelling_coverage)}</div>}
            {home.wind_deductible && <div><span className="text-[#888]">Wind Ded:</span> {formatDeductible(home.wind_deductible, home.wind_deductible_pct, home.dwelling_coverage)}</div>}
          </div>
        </div>
      )}

      {/* Life specifics */}
      {life && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5">Life Policy Details</div>
          <div className="grid grid-cols-4 gap-x-5 gap-y-1.5">
            {life.policy_type && <div><span className="text-[#888]">Type:</span> {LIFE_TYPE_LABELS[life.policy_type] || life.policy_type}</div>}
            {life.face_amount > 0 && <div><span className="text-[#888]">Face Amt:</span> ${life.face_amount.toLocaleString()}</div>}
            {life.term_length_years > 0 && <div><span className="text-[#888]">Term:</span> {life.term_length_years} yrs</div>}
            {life.level_term_through && <div><span className="text-[#888]">Level Thru:</span> {life.level_term_through}</div>}
            {life.issue_age > 0 && <div><span className="text-[#888]">Issue Age:</span> {life.issue_age}</div>}
            {life.issue_date && <div><span className="text-[#888]">Issued:</span> {life.issue_date}</div>}
            {life.premium_amount > 0 && <div><span className="text-[#888]">Premium:</span> ${life.premium_amount.toLocaleString()}{life.premium_mode && <span className="text-[#888]">/{PREMIUM_MODE_SHORT[life.premium_mode] || life.premium_mode}</span>}</div>}
            {life.premium_payable_until_age > 0 && <div><span className="text-[#888]">Pay Until Age:</span> {life.premium_payable_until_age}</div>}
            {life.cash_value > 0 && <div><span className="text-[#888]">Cash Value:</span> ${life.cash_value.toLocaleString()}</div>}
            {life.surrender_value > 0 && <div><span className="text-[#888]">Surrender:</span> ${life.surrender_value.toLocaleString()}</div>}
            {life.loan_balance > 0 && <div><span className="text-[#888]">Loan Bal:</span> ${life.loan_balance.toLocaleString()}</div>}
            {life.dividend_option && <div><span className="text-[#888]">Dividends:</span> {life.dividend_option.replace(/_/g, ' ')}</div>}
            {life.rating_class && <div><span className="text-[#888]">Class:</span> {life.rating_class.replace(/_/g, ' ')}</div>}
            {life.tobacco_use != null && <div><span className="text-[#888]">Tobacco:</span> {life.tobacco_use ? 'Yes' : 'No'}</div>}
            {life.convertible && <div><span className="text-[#888]">Convertible:</span> Yes{life.conversion_expires && ` (until ${life.conversion_expires})`}</div>}
            {life.owner_name && <div><span className="text-[#888]">Owner:</span> {life.owner_name}</div>}
          </div>
          {life.beneficiaries && life.beneficiaries.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1">Beneficiaries</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                {life.beneficiaries.map((b, i) => (
                  <span key={i}>
                    {b.name}
                    {b.relation && <span className="text-[#888]"> ({b.relation})</span>}
                    {b.share_pct > 0 && <span className="text-[#888]"> {b.share_pct}%</span>}
                    {b.type && b.type !== 'primary' && <span className="text-[#888]"> [{b.type}]</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
          {life.riders && life.riders.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1">Riders</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                {life.riders.map((r, i) => (
                  <span key={i}>
                    {r.name}
                    {r.amount > 0 && <span className="text-[#888]"> · ${r.amount.toLocaleString()}</span>}
                    {r.premium > 0 && <span className="text-[#888]"> · ${r.premium}/yr</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Disability specifics */}
      {dis && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5">Disability Policy Details</div>
          <div className="grid grid-cols-4 gap-x-5 gap-y-1.5">
            {dis.policy_type && <div><span className="text-[#888]">Type:</span> {dis.policy_type.replace(/_/g, ' ')}</div>}
            {dis.monthly_benefit > 0 && <div><span className="text-[#888]">Monthly Benefit:</span> ${dis.monthly_benefit.toLocaleString()}</div>}
            {dis.elimination_period_days > 0 && <div><span className="text-[#888]">Elim Period:</span> {dis.elimination_period_days}d</div>}
            {dis.benefit_period && <div><span className="text-[#888]">Benefit Period:</span> {dis.benefit_period.replace(/_/g, ' ')}</div>}
            {dis.definition_of_disability && <div><span className="text-[#888]">Definition:</span> {dis.definition_of_disability.replace(/_/g, ' ')}</div>}
            {dis.occupation_class && <div><span className="text-[#888]">Occ Class:</span> {dis.occupation_class}</div>}
            {dis.premium_amount > 0 && <div><span className="text-[#888]">Premium:</span> ${dis.premium_amount.toLocaleString()}{dis.premium_mode && <span className="text-[#888]">/{PREMIUM_MODE_SHORT[dis.premium_mode] || dis.premium_mode}</span>}</div>}
            {dis.non_cancellable && <div><span className="text-[#888]">Non-cancellable</span></div>}
            {dis.guaranteed_renewable && <div><span className="text-[#888]">Guaranteed Renewable</span></div>}
            {dis.cola_rider && <div><span className="text-[#888]">COLA</span></div>}
            {dis.future_increase_option && <div><span className="text-[#888]">Future Increase Option</span></div>}
            {dis.residual_disability && <div><span className="text-[#888]">Residual</span></div>}
          </div>
          {dis.riders && dis.riders.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1">Riders</div>
              <div className="flex flex-wrap gap-x-5 gap-y-1">
                {dis.riders.map((r, i) => (
                  <span key={i}>
                    {r.name}
                    {r.amount > 0 && <span className="text-[#888]"> · ${r.amount.toLocaleString()}</span>}
                    {r.premium > 0 && <span className="text-[#888]"> · ${r.premium}/yr</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Prior carrier */}
      {(ed.prior_carrier || ed.prior_policy_premium) && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5">Prior Coverage</div>
          <div>
            {ed.prior_carrier && <span>{ed.prior_carrier}</span>}
            {ed.prior_policy_premium && <span className="text-[#888]"> · ${ed.prior_policy_premium.toLocaleString()}</span>}
          </div>
        </div>
      )}

      {/* Notes */}
      {(p.notes || ed.extraction_notes) && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5">Notes</div>
          <div className="text-[#666] italic">{p.notes || ed.extraction_notes}</div>
        </div>
      )}
    </div>
  );
}
